import { effect, Injectable, signal } from '@angular/core';
import {
  PatientBehaviorModel,
  PatientPlaylistItemModel,
  PatientPlaylistModel,
  PatientProgramModel,
  PatientReminderModel,
  PatientTargetModel,
  PromptScheduleModel,
  SkillAreaModel,
  SkillAcquisitionObjectiveModel,
  TreatmentGoalModel,
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  TargetType,
  PatientTargetStatus,
  BehaviorGoalModel,
} from '../core/api/models';

@Injectable({
  providedIn: 'root',
})
export class SupervisorHelperService {
  updateSignal = signal(0);

  playlists = signal<PatientPlaylistModel[]>([]);
  skillAreas = signal<SkillAreaModel[]>([]);
  skillAcquisitionGoals = signal<SkillAcquisitionObjectiveModel[]>([]);
  behaviorGoals = signal<BehaviorGoalModel[]>([]);
  programs = signal<PatientProgramModel[]>([]);
  targets = signal<PatientTargetModel[]>([]);
  behaviors = signal<PatientBehaviorModel[]>([]);
  promptSchedules = signal<PromptScheduleModel[]>([]);
  treatmentGoals = signal<TreatmentGoalModel[]>([]);
  showAddProgram = signal(false);
  showAddStaffAlert = signal(false);
  showAddPlaylist = signal(false);
  showAddTarget = signal(false);
  showAddSkillArea = signal(false);
  showAddReminder = signal(false);
  showAddBehavior = signal(false);
  showAddAntecedent = signal(false);
  showAddConsequence = signal(false);
  programToEdit = signal<PatientProgramModel | null>(null);
  playlistToEdit = signal<PatientPlaylistModel | null>(null);
  targetToEdit = signal<PatientTargetModel | null>(null);
  addingTargetToProgramID = signal<number | null>(null);
  skillAreaToEdit = signal<SkillAreaModel | null>(null);
  reminderToEdit = signal<PatientReminderModel | null>(null);
  behaviorToEdit = signal<PatientBehaviorModel | null>(null);
  antecedentToEdit = signal<PatientBehaviorAntecedentModel | null>(null);
  consequenceToEdit = signal<PatientBehaviorConsequenceModel | null>(null);
  lastCreatedTarget = signal<PatientTargetModel | null>(null);

  constructor() {
    console.log('SupervisorHelperService constructor');
    effect(() => {
      console.log('update signal triggered: ', this.updateSignal());
    });
  }

  onCloseAddPlaylist(withChanges: boolean = false) {
    this.showAddPlaylist.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddTarget(withChanges: boolean = false) {
    this.showAddTarget.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddProgram(withChanges: boolean = false) {
    this.showAddProgram.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddStaffAlert(withChanges: boolean = false) {
    this.showAddStaffAlert.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddAntecedent(withChanges: boolean = false) {
    this.showAddAntecedent.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddConsequence(withChanges: boolean = false) {
    this.showAddConsequence.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddSkillArea(withChanges: boolean = false) {
    this.showAddSkillArea.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddReminder(withChanges: boolean = false) {
    this.showAddReminder.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  onCloseAddBehavior(withChanges: boolean = false) {
    this.showAddBehavior.set(false);
    if (withChanges) {
      this.updateSignal.update((oldValue) => oldValue + 1);
    }
  }

  additProgramButtonClicked(programToEdit: PatientProgramModel | null) {
    this.programToEdit.set(programToEdit);
    this.showAddProgram.set(true);
  }

  additPlaylistButtonClicked(playlistToEdit: PatientPlaylistModel | null) {
    this.playlistToEdit.set(playlistToEdit);
    this.showAddPlaylist.set(true);
  }

  additTargetButtonClicked(targetToEdit: PatientTargetModel | null, addingTargetToProgramID: number | null | undefined=undefined) {
    this.targetToEdit.set(targetToEdit);
    this.addingTargetToProgramID.set(addingTargetToProgramID??null);
    this.showAddTarget.set(true);
  }

  addAntecedentButtonClicked(
    antecedentToEdit: PatientBehaviorAntecedentModel | null
  ) {
    this.antecedentToEdit.set(antecedentToEdit);
    this.showAddAntecedent.set(true);
  }

  staffAlertButtonClicked() {
    this.showAddStaffAlert.set(true);
  }

  addSkillAreaButtonClicked(skillAreaToEdit: SkillAreaModel | null) {
    this.skillAreaToEdit.set(skillAreaToEdit);
    this.showAddSkillArea.set(true);
  }

  addReminderButtonClicked(reminderToEdit: PatientReminderModel | null) {
    this.reminderToEdit.set(reminderToEdit);
    this.showAddReminder.set(true);
  }

  addBehaviorButtonClicked(behaviorToEdit: PatientBehaviorModel | null) {
    this.behaviorToEdit.set(behaviorToEdit);
    this.showAddBehavior.set(true);
  }

  addConsequenceButtonClicked(
    consequenceToEdit: PatientBehaviorConsequenceModel | null
  ) {
    this.consequenceToEdit.set(consequenceToEdit);
    this.showAddConsequence.set(true);
  }

  isPatientTargetModel(filter: any): filter is PatientTargetModel {
    return (filter as PatientTargetModel).TargetName !== undefined;
  }

  isSkillAreaModel(filter: any): filter is SkillAreaModel {
    return (filter as SkillAreaModel).SkillAreaName !== undefined;
  }

  isPatientBehaviorModel(filter: any): filter is PatientBehaviorModel {
    return (filter as PatientBehaviorModel).PatientBehaviorName !== undefined;
  }

  isPatientProgramModel(filter: any): filter is PatientProgramModel {
    return (filter as PatientProgramModel).ProgramName !== undefined;
  }

  isPatientPlaylistModel(filter: any): filter is PatientPlaylistModel {
    return (filter as PatientPlaylistModel).PlaylistName !== undefined;
  }

  isPatientPlaylistItemModel(filter: any): filter is PatientPlaylistItemModel {
    return (filter as PatientPlaylistItemModel).PatientTargetID !== undefined;
  }

  isTargetType(filter: any): filter is TargetType {
    return Object.values(TargetType).includes(filter);
  }

  isPatientTargetStatus(filter: any): filter is PatientTargetStatus {
    return Object.values(PatientTargetStatus).includes(filter);
  }

  triggerUpdateSignal() {
    this.updateSignal.update((oldValue) => oldValue + 1);
  }
}
